'use client';

import * as React from 'react';
import {Button} from '@purpose/ui/components/button';
import {cn} from '@purpose/ui/lib/utils';
import {Check} from 'lucide-react';
import {
  Sheet,
  SheetTitle,
  SheetContent,
  SheetHeader
} from '@purpose/ui/components/sheet/sheet';
import {StatusFilter, FilterSectionAccordion, CategoryTree} from './components';
import {RadioGroup, RadioGroupItem} from '../radio-group/radio-group';
import {Label} from '../label/label';
import {Box} from '../box';
import {Input} from '../input/input';
import {RangeSlider} from '../range-slider/range-slider';
import Divider from '../divider/divider';
import {Checkbox} from '../checkbox/checkbox';
import {Category, Status} from './types';
import {ImageWithFallback} from '../ImageWithFallback';
import useInfiniteScroll from '@purpose/ui/hooks/useInfiniteScroll';

export enum DiscountTypeVariant {
  percentage = 'percentage',
  amount = 'amount',
  none = 'none'
}

export enum BenefitTypeVariant {
  any = 'any',
  public = 'public',
  private = 'private'
}

export type DiscountVariant =
  | DiscountTypeVariant.percentage
  | DiscountTypeVariant.amount
  | DiscountTypeVariant.none;

export type DiscountTypeFilterValue = {
  type: DiscountVariant;
  minValue?: number;
  maxValue?: number;
};

export type StatusFilterValue = Status;
export type CategoriesFilterValue = number[];
export type EntitiesFilterValue = number[];

export interface FiltersState {
  discountType: DiscountTypeFilterValue;
  status: StatusFilterValue[] | null;
  categories: CategoriesFilterValue | null;
  entities: EntitiesFilterValue | null;
  type: BenefitTypeVariant | null;
}

type CategoryTypesMap = Record<
  string,
  {
    id: number;
    titleId: string;
    icon: React.ElementType | null;
  }
>;

export interface BenefitFiltersDrawerProps {
  onClose: () => void;
  isOpen: boolean;
  onAccept: (filters: FiltersState) => void;
  categories: Category[];
  entitiesFilter: {
    list: {
      id: number;
      name: string;
      photo: string;
    }[];
    title: string;
    fetchNextPage?: (options?: any) => void;
    hasMore?: boolean;
  };
  filters: FiltersState;
  setFilters: React.Dispatch<React.SetStateAction<FiltersState>>;
  categoriesTypesMap: CategoryTypesMap;
  t: (key: string) => string;
}

const BenefitFiltersDrawer = ({
  onClose,
  isOpen,
  onAccept,
  t,
  categories,
  entitiesFilter,
  filters,
  setFilters,
  categoriesTypesMap
}: BenefitFiltersDrawerProps) => {
  const [categorySearchValue, setCategorySearchValue] = React.useState('');
  const [entitySearchValue, setEntitySearchValue] = React.useState('');

  const loadMoreRef = useInfiniteScroll({
    loadMore: entitiesFilter.fetchNextPage,
    hasMore: !!entitiesFilter?.hasMore
  });

  const isCategoryTitleMatchSearch = category =>
    (t(categoriesTypesMap[category.key]?.titleId as string) || category.name)
      .toLowerCase()
      .includes(categorySearchValue.toLocaleLowerCase());

  const filterCategories = (list: Category[]): Category[] => {
    return list
      .map(category => {
        const matchesSelf = isCategoryTitleMatchSearch(category);
        if (matchesSelf) return category;

        const filteredChildren = category.children
          ? filterCategories(category.children)
          : [];
        if (filteredChildren.length) {
          return {...category, children: filteredChildren};
        }
        return null;
      })
      .filter(Boolean) as Category[];
  };

  const filteredBySearchCategories = React.useMemo(() => {
    if (!categorySearchValue) return categories;
    return filterCategories(categories);
  }, [categorySearchValue, categories]);

  const filteredBySearchEntities = React.useMemo(
    () =>
      entitySearchValue?.length
        ? entitiesFilter.list.filter(({name}) =>
            name.toLowerCase().includes(entitySearchValue.toLocaleLowerCase())
          )
        : entitiesFilter.list,
    [entitySearchValue, entitiesFilter]
  );

  const handleAccept = () => {
    onAccept(filters);
    onClose();
  };

  const handleReset = () => {
    setFilters({
      discountType: {
        type: DiscountTypeVariant.none
      },
      status: null,
      categories: null,
      entities: null,
      type: null
    });
  };

  const defaultRangeValues = {
    min: filters.discountType?.type === 'percentage' ? 0 : undefined,
    max: filters.discountType?.type === 'percentage' ? 100 : undefined
  };

  const onStatusChange = (status: StatusFilterValue) => {
    if (filters.status?.find(s => s === status)) {
      setFilters(prev => ({
        ...prev,
        status: prev.status?.filter(s => s !== status) || null
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        status: [...(prev.status || []), status]
      }));
    }
  };

  const onSliderChange = (range: [number, number]) => {
    setFilters(prev => ({
      ...prev,
      discountType: {
        ...(prev.discountType ?? {
          type: DiscountTypeVariant.none
        }),
        minValue: range[0],
        maxValue: range[1]
      }
    }));
  };

  const onDiscountTypeChange = (type: DiscountTypeVariant) => {
    setFilters(prev => ({
      ...prev,
      discountType: {
        type,
        ...(type === DiscountTypeVariant.percentage && {
          minValue: 0,
          maxValue: 100
        })
      }
    }));
  };

  const onBenefitTypeChange = (type: BenefitTypeVariant) => {
    setFilters(prev => ({...prev, type}));
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-100 overflow-y-scroll sm:w-135">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            {t('BenefitFiltersDrawer.title')}
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-6 py-6">
          {/* Discount filter */}
          <div className="flex flex-col gap-3">
            <FilterSectionAccordion
              title={t('BenefitFiltersDrawer.discountType.title')}
              isActive={
                !!filters.discountType?.minValue ||
                !!filters.discountType?.maxValue
              }
            >
              <RadioGroup
                defaultValue={DiscountTypeVariant.none}
                value={filters.discountType?.type}
                onValueChange={onDiscountTypeChange}
                className="mt-3 ml-2 flex flex-col gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={DiscountTypeVariant.none}
                    id={DiscountTypeVariant.none}
                  />
                  <Label htmlFor={DiscountTypeVariant.none}>
                    {t('BenefitFiltersDrawer.discountType.notSelected')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={DiscountTypeVariant.percentage}
                    id={DiscountTypeVariant.percentage}
                  />
                  <Label htmlFor={DiscountTypeVariant.percentage}>
                    {t('BenefitFiltersDrawer.discountType.percentage')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={DiscountTypeVariant.amount}
                    id={DiscountTypeVariant.amount}
                  />
                  <Label htmlFor={DiscountTypeVariant.amount}>
                    {t('BenefitFiltersDrawer.discountType.amount')}
                  </Label>
                </div>
              </RadioGroup>

              {filters.discountType?.type !== DiscountTypeVariant.none && (
                <div className="bg-secondary-0 border-secondary-10 mt-6 flex flex-col rounded-[10px] border border-solid px-4 py-5">
                  <Box className="flex flex-col items-center gap-5">
                    <div className="flex w-full flex-row items-center justify-between">
                      <Box className="w-25">
                        <Input
                          className={cn('bg-secondary-0 p-2.5')}
                          placeholder="Min"
                          value={
                            filters.discountType?.minValue !== undefined
                              ? filters.discountType.minValue
                              : defaultRangeValues.min
                          }
                          onChange={e => {
                            if (Number.isNaN(parseFloat(e.target.value))) {
                              setFilters(prev => ({
                                ...prev,
                                discountType: {
                                  ...prev.discountType,
                                  minValue: defaultRangeValues.min
                                }
                              }));
                              return;
                            }
                            if (
                              filters.discountType?.type === 'percentage' &&
                              (parseFloat(e.target.value) < 1 ||
                                parseFloat(e.target.value) > 99)
                            )
                              return;
                            setFilters(prev => ({
                              ...prev,
                              discountType: {
                                ...prev.discountType,
                                minValue: parseFloat(e.target.value)
                              }
                            }));
                          }}
                          endAdornment={
                            <p>
                              {filters.discountType?.type === 'percentage'
                                ? '%'
                                : 'kr'}
                            </p>
                          }
                        />
                      </Box>
                      <Box className="w-25">
                        <Input
                          className={cn('bg-secondary-0 p-2.5')}
                          placeholder="Max"
                          value={
                            filters.discountType?.maxValue !== undefined
                              ? filters.discountType.maxValue
                              : defaultRangeValues.max
                          }
                          onChange={e => {
                            if (Number.isNaN(parseFloat(e.target.value))) {
                              setFilters(prev => ({
                                ...prev,
                                discountType: {
                                  ...prev.discountType,
                                  maxValue: defaultRangeValues.max
                                }
                              }));
                              return;
                            }

                            if (
                              filters.discountType?.type === 'percentage' &&
                              (parseFloat(e.target.value) < 1 ||
                                parseFloat(e.target.value) > 99)
                            )
                              return;
                            setFilters(prev => ({
                              ...prev,
                              discountType: {
                                ...prev.discountType,
                                maxValue: parseFloat(e.target.value)
                              }
                            }));
                          }}
                          endAdornment={
                            <p>
                              {filters.discountType?.type === 'percentage'
                                ? '%'
                                : 'kr'}
                            </p>
                          }
                        />
                      </Box>
                    </div>
                    {filters.discountType?.type === 'percentage' && (
                      <RangeSlider
                        className="h-2"
                        step={1}
                        defaultValue={[1, 100]}
                        value={[
                          filters.discountType?.minValue || 1,
                          filters.discountType?.maxValue || 100
                        ]}
                        min={1}
                        max={100}
                        onValueChange={onSliderChange}
                        minStepsBetweenThumbs={1}
                      />
                    )}
                  </Box>
                </div>
              )}
            </FilterSectionAccordion>
          </div>
          <Divider />
          {/* Type filter */}
          <div className="flex flex-col gap-3">
            <FilterSectionAccordion
              title={t('BenefitFiltersDrawer.type.title')}
              isActive={!!filters.type}
            >
              <RadioGroup
                defaultValue={BenefitTypeVariant.any}
                value={filters.type}
                onValueChange={onBenefitTypeChange}
                className="mt-3 ml-2 flex flex-col gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={BenefitTypeVariant.any}
                    id={BenefitTypeVariant.any}
                  />
                  <Label htmlFor={BenefitTypeVariant.any}>
                    {t('BenefitFiltersDrawer.type.any')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={BenefitTypeVariant.public}
                    id={BenefitTypeVariant.public}
                  />
                  <Label htmlFor={BenefitTypeVariant.public}>
                    {t('BenefitFiltersDrawer.type.public')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={BenefitTypeVariant.private}
                    id={BenefitTypeVariant.private}
                  />
                  <Label htmlFor={BenefitTypeVariant.private}>
                    {t('BenefitFiltersDrawer.type.private')}
                  </Label>
                </div>
              </RadioGroup>
            </FilterSectionAccordion>
          </div>
          <Divider />
          {/* Status filter */}
          <div className="flex flex-col gap-3">
            <FilterSectionAccordion
              title={t('BenefitFiltersDrawer.status.title')}
              isActive={!!filters.status?.length}
            >
              <StatusFilter
                t={t}
                selectedValues={(filters.status || []) as StatusFilterValue[]}
                onChange={onStatusChange}
              />
            </FilterSectionAccordion>
          </div>
          <Divider />

          {/* Categories Filter */}
          <div className="flex flex-col gap-3">
            <FilterSectionAccordion
              title={t('BenefitFiltersDrawer.categories.title')}
              isActive={!!filters.categories?.length}
            >
              <Input
                className="my-2"
                placeholder={t('BenefitFiltersDrawer.Search')}
                value={categorySearchValue}
                onChange={e => setCategorySearchValue(e.target.value)}
              />
              <CategoryTree
                categoriesTypesMap={categoriesTypesMap}
                t={t}
                setFilters={setFilters}
                filters={filters}
                categories={filteredBySearchCategories}
              />
            </FilterSectionAccordion>
          </div>
          {entitiesFilter.title && (
            <>
              <Divider />

              {/* Entities Filter */}
              <div>
                <FilterSectionAccordion
                  title={t(entitiesFilter.title)}
                  isActive={!!filters.entities?.length}
                >
                  <Input
                    className="my-2"
                    placeholder={t('BenefitFiltersDrawer.Search')}
                    value={entitySearchValue}
                    onChange={e => setEntitySearchValue(e.target.value)}
                  />
                  <div className="max-h-96.25 space-y-2 overflow-scroll">
                    {filteredBySearchEntities.map(entity => (
                      <label
                        key={entity.id}
                        className="flex cursor-pointer items-center"
                      >
                        <Checkbox
                          checked={
                            filters.entities?.includes(entity.id) || false
                          }
                          onCheckedChange={value => {
                            const currentEntities = filters.entities || [];
                            if (value) {
                              setFilters(prev => ({
                                ...prev,
                                entities: [...currentEntities, entity.id]
                              }));
                            } else {
                              setFilters(prev => ({
                                ...prev,
                                entities: currentEntities.filter(
                                  id => id !== entity.id
                                )
                              }));
                            }
                          }}
                        />
                        <ImageWithFallback
                          width={40}
                          height={40}
                          iconStyles={{width: 24, height: 24}}
                          src={entity.photo}
                          alt="logo"
                          containerStyle={{
                            marginRight: '8px',
                            marginLeft: '16px',
                            borderRadius: '40px'
                          }}
                        />
                        <span className="text-sm">{entity.name}</span>
                      </label>
                    ))}
                  </div>
                  <div ref={loadMoreRef} />
                </FilterSectionAccordion>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="h-10">
          <div className="fixed bottom-0 flex w-88 gap-3 bg-white py-6 sm:w-83.75">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleReset}
              aria-label={t('BenefitFiltersDrawer.ResetAll')}
            >
              {t('BenefitFiltersDrawer.ResetAll')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleAccept}
              disabled={
                !filters.discountType?.minValue &&
                !filters.discountType?.maxValue &&
                !filters.categories?.length &&
                !filters.entities?.length &&
                !filters.status?.length &&
                !filters?.type
              }
              aria-label={t('BenefitFiltersDrawer.Apply')}
            >
              <Check className="mr-2 h-4 w-4" />
              {t('BenefitFiltersDrawer.Apply')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export {BenefitFiltersDrawer};
